import { useEffect, useMemo, useRef, useState } from 'react';
import { BiChevronRight, BiCopy, BiErrorCircle, BiTrash } from 'react-icons/bi';
import { BsPencil, BsThreeDots, BsTrash } from 'react-icons/bs';
import { FaRegCalendar } from 'react-icons/fa6';
import { HiOutlineUsers } from 'react-icons/hi2';
import { IoMdClose } from 'react-icons/io';
import {
  MdOutlineCancel,
  MdOutlineCheckCircle,
  MdOutlineRestartAlt,
  MdRestaurantMenu,
} from 'react-icons/md';
import { RiShip2Line } from 'react-icons/ri';
import ReactMarkdown from 'react-markdown';
import { Link, useNavigate } from 'react-router-dom';

import { API, getRequestBlob } from '@/api/api.ts';
import { imageVariantLink } from '@/common/media';
import { AuthorizedImage } from '@/components/AuthorizedImage';
import { Button, ButtonOutlined } from '@/components/Button';
import {
  isCancelledItem,
  isCompletedItem,
  notifyEventActionError,
} from '@/components/Calendar/calendarUtils';
import type { CreateTaskPayload } from '@/components/CreateSheet/CreateEditTaskSheet';
import CreateEditTaskSheet from '@/components/CreateSheet/CreateEditTaskSheet';
import CreateItinerarySheet from '@/components/CreateSheet/EditEventSheet/ItinerarySection/CreateItinerarySheet/CreateItinerarySheet';
import { DateTimePicker } from '@/components/DateTimePicker';
import { DeclinedBadge } from '@/components/DeclinedBadge/DeclinedBadge';
import { DocumentPreviewModal } from '@/components/DocumentPreview/DocumentPreview';
import { EventDateGroup } from '@/components/EventDateGroup/EventDateGroup';
import { PendingBadge } from '@/components/PendingBadge/PendingBadge';
import { StatusPill } from '@/components/StatusPill/StatusPill';
import { useInviteRights } from '@/hooks/useInviteRights';
import { usePersonsMap } from '@/hooks/usePersonsMap';
import { PERMISSIONS } from '@/permissions/permissions.catalog';
import { usePermissions } from '@/permissions/usePermissions';
import type { InviteEmailTemplate } from '@/store/auth/auth.dto';
import { useAuth } from '@/store/auth/auth.store';
import {
  deriveEventStatus,
  EVENT_TYPES,
  type EventResponse,
  EVENTS,
  getParticipantDeclineReason,
  getParticipantStatus,
  headcount,
  isDated,
  isIdeaTrip,
  partyHeadcount,
  type SubActivity,
  type SubEventInput,
  sumHeadcount,
} from '@/store/events/events.dto';
import { deleteEventParticipantsApi, useEvents } from '@/store/events/events.store';
import {
  type GuestInvitee,
  type PendingGuestInvite,
  pendingGuestInvites,
  useDeleteEventInviteAnswerMutation,
  useEventInviteAnswersQuery,
  useEventInvitesQuery,
  useGuestCountActions,
  useGuestInviteActions,
  useInviteGuestsMutation,
} from '@/store/invites/invites.store';
import type { EventInviteAnswer } from '@/store/invites/invites.types';
import { usePerson } from '@/store/persons/person.store';
import { type TaskResponse, TaskStatus } from '@/store/tasks/tasks.dto';
import { useTasks } from '@/store/tasks/tasks.store';
import { useVessels } from '@/store/vessels/vessels.store';
import { downloadBlob } from '@/utils/download-file-from-link';
import { formatEventDateRange } from '@/utils/formatEventDateRange';
import { groupItemsByDate } from '@/utils/groupByDate';
import { toast } from '@/utils/toast.ts';
import MenuIcon from '@assets/menu.svg?react';

import { Accordion } from '../Accordion/Accordion';
import { ConfirmationModal } from '../ConfirmationModal';
import ContextMenu from '../ContextMenu/ContextMenu';
import { GuestInvitationModal } from '../GuestInvitationModal/GuestInvitationModal';
import {
  Carousel,
  CAROUSEL_SLIDE_HEIGHT,
  CAROUSEL_SLIDE_WIDTH,
  type CarouselImage,
  getCarouselImageSrc,
  getCarouselSlideSrc,
} from '../ImageCarousel';
import Modal from '../Modal/Modal';
import { SidePanel } from '../SidePanel';
import { EventItem } from '../Widget/UpcomingEventWidget/EventItem';

import { DeclineReasonModal } from './DeclineReasonModal';
import { EventChatButtons } from './EventChatButtons';
import s from './EventSidePanel.module.scss';
import { GuestsSection } from './GuestsSection';
import { InviteLinkButton } from './InviteLinkButton';
import SubActivitySidePanel from './SubActivitySidePanel';
import { TagsSection } from './TagsSection';
import { TasksSection } from './TasksSection';

const TYPE_ICONS: Partial<Record<EVENTS, typeof FaRegCalendar>> = {
  [EVENTS.OUT_OF_WATER]: RiShip2Line,
  [EVENTS.VOYAGE]: RiShip2Line,
  [EVENTS.TRIP]: RiShip2Line,
  [EVENTS.GUEST]: HiOutlineUsers,
  [EVENTS.FOOD]: MdRestaurantMenu,
  [EVENTS.GENERAL]: FaRegCalendar,
};

interface EventSidePanelProps {
  open: boolean;
  onClose: () => void;
  event: EventResponse | null;
  onEdit?: (eventId: string) => void;
  onDelete?: (eventId: string) => void;
  /** Offered as "Plan trip like this" — only where the caller can seed a new wizard. */
  onDuplicate?: (eventId: string) => void;
  onEditSubActivity?: (id: string, data: SubEventInput) => void;
  onTaskStatusChange?: (taskId: string, serviceId: string, status: TaskStatus) => void;
  /** Fires when a chat is opened from this panel, so the caller can close what it stacked underneath. */
  onChatOpen?: () => void;
}

function EventSidePanel({
  open,
  onClose,
  event,
  onEdit,
  onDelete,
  onDuplicate,
  onEditSubActivity,
  onTaskStatusChange,
  onChatOpen,
}: EventSidePanelProps) {
  const personsMap = usePersonsMap();
  const { createTask, updateTask, removeTask, getTasksByEventId, updateTaskStatus } = useTasks();
  // Re-read event tasks whenever the global task list changes (e.g. tasks added via the Edit Event modal)
  const tasksList = useTasks((s) => s.tasksList);
  const [tasks, setTasks] = useState<TaskResponse[]>([]);
  const [editingTask, setEditingTask] = useState<TaskResponse | null>(null);
  const [taskToDelete, setTaskToDelete] = useState<string | null>(null);
  const [subActivityToDelete, setSubActivityToDelete] = useState<string | null>(null);
  const [subActivityToEdit, setSubActivityToEdit] = useState<SubActivity | null>(null);

  const handleTaskStatusChange = (taskId: string, serviceId: string, status: TaskStatus) => {
    setTasks((prev) => prev.map((task) => (task.id === taskId ? { ...task, status } : task)));

    if (onTaskStatusChange) {
      void onTaskStatusChange(taskId, serviceId, status);
      return;
    }

    void updateTaskStatus({ id: taskId, serviceId, status });
  };

  useEffect(() => {
    if (!open || !event?.id) return;
    let cancelled = false;
    getTasksByEventId(event.id).then((result) => {
      if (!cancelled) setTasks(result);
    });
    return () => {
      cancelled = true;
    };
  }, [open, event?.id, getTasksByEventId, tasksList]);

  const [previewAttachment, setPreviewAttachment] = useState<{ name: string; src: string } | null>(
    null,
  );
  const [menuAnchor, setMenuAnchor] = useState<HTMLButtonElement | null>(null);
  const [selectedSubActivityId, setSelectedSubActivityId] = useState<string | null>(null);
  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [isGuestInvitationOpen, setIsGuestInvitationOpen] = useState(false);
  const [isTaskSheetOpen, setIsTaskSheetOpen] = useState(false);
  const [isGuestsOpen, setIsGuestsOpen] = useState(false);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [promoteStart, setPromoteStart] = useState<Date | null>(null);
  const [promoteEnd, setPromoteEnd] = useState<Date | null>(null);
  const [promoteSubmitting, setPromoteSubmitting] = useState(false);
  const subActivityToDecline = useRef<string | null>(null);
  const navigate = useNavigate();

  const { can, canAny } = usePermissions();
  const canActOnEvents = can(PERMISSIONS.activity.EventsActions);
  const canViewMenu = canAny([PERMISSIONS.menu.GetMenuInfo, PERMISSIONS.menu.EditMenu]);
  const isGuest = !canActOnEvents;
  const isOwnerOrCaptain = canActOnEvents;
  const { canInvite } = useInviteRights();
  const currentUserId = useAuth((s) => s.session?.id);
  const { mutateAsync: inviteGuests } = useInviteGuestsMutation();
  const { vessel } = useVessels();
  const setParticipantStatus = useEvents((s) => s.setParticipantStatus);
  const cancelEvent = useEvents((s) => s.cancelEvent);
  const renewEvent = useEvents((s) => s.renewEvent);
  const getEventById = useEvents((s) => s.getEventById);
  const deleteSubActivity = useEvents((s) => s.deleteSubActivity);
  const updateSubActivity = useEvents((s) => s.updateSubActivity);
  const getPeople = usePerson((s) => s.getPeople);

  // Refetch fresh detail + people on open so a guest who accepted since last open
  // (new participant status AND possibly a newly-created person) is reflected. The detail is
  // read from the event's own space, since a personal event is not on the active vessel.
  useEffect(() => {
    if (!open || !event?.id) return;
    void getEventById(event.id, event.vessel_id);
    void getPeople();
  }, [open, event?.id, event?.vessel_id, getEventById, getPeople]);

  // When the user comes back to this panel while it is open, re-fetch only this
  // event so remote participant status changes (e.g. a guest declining in another
  // session) are picked up without requiring a close/reopen.
  // Both signals are needed: `visibilitychange` covers a backgrounded tab, while
  // `focus` covers switching between two windows side by side — that leaves the
  // tab "visible", so visibilitychange alone never fires for it.
  useEffect(() => {
    if (!open || !event?.id) return;
    const refetch = () => {
      if (document.visibilityState === 'visible') {
        void getEventById(event.id, event.vessel_id);
        void getPeople();
      }
    };
    document.addEventListener('visibilitychange', refetch);
    window.addEventListener('focus', refetch);
    return () => {
      document.removeEventListener('visibilitychange', refetch);
      window.removeEventListener('focus', refetch);
    };
  }, [open, event?.id, event?.vessel_id, getEventById, getPeople]);

  const handleEditTask = (taskId: string) => {
    const task = tasks.find((t) => t.id === taskId);
    if (task) {
      setEditingTask(task);
      setIsTaskSheetOpen(true);
    }
  };

  const confirmDeleteSubActivity = async () => {
    if (!subActivityToDelete) return;

    try {
      await deleteSubActivity(subActivityToDelete, event?.id);
      toast.success('Activity deleted');
    } catch {
      toast.error('Couldn’t delete the activity. Please try again.');
    } finally {
      setSubActivityToDelete(null);
    }
  };

  const confirmDeleteTask = async () => {
    if (!taskToDelete) return;
    await removeTask(taskToDelete);
    setTasks((prev) => prev.filter((t) => t.id !== taskToDelete));
    setTaskToDelete(null);
  };

  const decline = (eventId: string, reason?: string) =>
    setParticipantStatus(eventId, 'declined', reason);

  const handleAccept = async () => {
    if (!event) return;
    const subs = (event.sub_activities ?? []).map((sa) => setParticipantStatus(sa.id, 'accepted'));
    await Promise.all([setParticipantStatus(event.id, 'accepted'), ...subs]);
    setMenuAnchor(null);
  };

  const handleDecline = async (reason?: string) => {
    if (!currentUserId || !event) return;

    if (subActivityToDecline.current) {
      await decline(subActivityToDecline.current, reason);
      subActivityToDecline.current = null;
    } else {
      const subActivityDeclines = (event.sub_activities ?? []).map((sa) => decline(sa.id, reason));
      await Promise.all([decline(event.id, reason), ...subActivityDeclines]);
      onClose();
    }

    setDeclineModalOpen(false);
  };

  const handleCancelEvent = async (reason?: string) => {
    if (!event) return;
    try {
      await cancelEvent(event.id, reason);
      toast.success('Event canceled');
    } catch (error) {
      notifyEventActionError(error, 'cancel');
    } finally {
      setCancelModalOpen(false);
    }
  };

  const handleRenewEvent = async () => {
    if (!event) return;
    setMenuAnchor(null);
    try {
      await renewEvent(event.id);
      toast.success('Event renewed');
    } catch (error) {
      notifyEventActionError(error, 'renew');
    }
  };

  const currentEvent = useEvents((s) => s.currentEvent);
  const displayEvent = currentEvent?.id === event?.id ? currentEvent : event;
  const updateEvent = useEvents((s) => s.updateEvent);

  const derivedStatus = displayEvent ? deriveEventStatus(displayEvent) : null;
  const canPromote = !!displayEvent && isIdeaTrip(displayEvent) && isOwnerOrCaptain;
  // same rule as the create wizard: no past dates
  const promoteMinDate = new Date();

  const openPromote = () => {
    setPromoteStart(null);
    setPromoteEnd(null);
    setPromoteOpen(true);
  };

  const handlePromote = async () => {
    if (!displayEvent || !promoteStart || !promoteEnd) return;
    setPromoteSubmitting(true);
    try {
      // Echo existing sub-activities back so the store's diff at
      // events.store.ts:459-478 finds no changes and doesn't DELETE them all.
      const subEvents: SubEventInput[] = (displayEvent.sub_activities ?? []).map((sa) => ({
        id: sa.id,
        title: sa.title,
        description: sa.description,
        time_slot: { start: sa.time_slot.start, end: sa.time_slot.end },
        participants: sa.participants,
      }));
      await updateEvent({
        id: displayEvent.id,
        parent_id: displayEvent.parent?.id,
        participants: displayEvent.participants,
        title: displayEvent.title,
        description: displayEvent.description,
        location: displayEvent.location,
        type: displayEvent.type,
        time_slot: {
          type: displayEvent.time_slot?.type ?? 'day_based',
          start: promoteStart.toISOString(),
          end: promoteEnd.toISOString(),
        },
        sub_events: subEvents,
      } as unknown as EventResponse);
      await getEventById(displayEvent.id, displayEvent.vessel_id);
      toast.success('Trip promoted to Planned');
      setPromoteOpen(false);
    } catch {
      toast.error('Couldn’t promote the trip. Please try again.');
    } finally {
      setPromoteSubmitting(false);
    }
  };

  const myMainStatus =
    currentUserId && displayEvent
      ? getParticipantStatus(displayEvent?.participants, currentUserId)
      : undefined;

  const myDeclineReason =
    currentUserId && displayEvent
      ? getParticipantDeclineReason(displayEvent.participants, currentUserId)
      : undefined;

  const isEventCancelled = !!displayEvent && isCancelledItem(displayEvent);
  const isEventCompleted = !!displayEvent && isCompletedItem(displayEvent);
  const isEventDeclined = isGuest && myMainStatus === 'declined';
  const isEventPending = isGuest && myMainStatus === 'unknown';
  const guestsIds =
    displayEvent?.participants?.filter((p) => p?.type === 'guest')?.map((p) => p?.person_id) ?? [];
  const guests = guestsIds
    .map((id) => personsMap.get(id))
    .filter((v): v is NonNullable<typeof v> => !!v);

  // Guests who declined before accepting never get a people_roles entry, and a guest
  // who just accepted is not in the people list until it is refetched, so personsMap
  // has no record for them. Synthesise Guest objects from the participant name.
  const inviteOnlyDeclinedGuests = (displayEvent?.participants ?? [])
    .filter((p) => p.type === 'guest' && !personsMap.has(p.person_id))
    .map((p) => {
      const [firstName = '', ...rest] = (p.name ?? '').split(' ');
      return { id: p.person_id, person_info: { first_name: firstName, last_name: rest.join(' ') } };
    });
  const allGuests = [...guests, ...inviteOnlyDeclinedGuests];

  // Pending guest invites for this event (persistent).
  const { data: guestInvites } = useEventInvitesQuery(event?.id, event?.vessel_id);
  const { data: answers = [] } = useEventInviteAnswersQuery(event?.id, event?.vessel_id);
  const pendingInvites = pendingGuestInvites(
    guestInvites?.items,
    new Set(allGuests.map((g) => g.id)),
    answers,
  );

  const selectedSubActivity = selectedSubActivityId
    ? (displayEvent?.sub_activities?.find((sa) => sa.id === selectedSubActivityId) ?? null)
    : null;
  const subActivityToDeleteTitle =
    displayEvent?.sub_activities?.find((sa) => sa.id === subActivityToDelete)?.title ?? '';
  const taskToDeleteTitle = tasks.find((task) => task.id === taskToDelete)?.title ?? '';

  const attachments = useMemo<CarouselImage[]>(
    () =>
      (displayEvent?.attachments ?? []).map(
        (a) =>
          ({
            type: 'attachment',
            id: a.id,
            src: a.link,
            thumb: imageVariantLink(a, CAROUSEL_SLIDE_WIDTH, CAROUSEL_SLIDE_HEIGHT) ?? undefined,
            name: a.name,
          }) as const,
      ),
    [displayEvent?.attachments],
  );

  const groupedSubActivities = useMemo(() => {
    if (!displayEvent?.sub_activities) return [];
    const sorted = [...displayEvent.sub_activities].sort((a, b) => {
      if (!a.time_slot?.start || !b.time_slot?.start) return 0;
      return new Date(a.time_slot.start).getTime() - new Date(b.time_slot.start).getTime();
    });
    return groupItemsByDate(sorted, (a) => new Date(a.time_slot.start));
  }, [displayEvent?.sub_activities]);

  const {
    cancel: handleCancelInvite,
    resend,
    changeDeadline,
    isResending,
  } = useGuestInviteActions();
  const { changeGuestCount } = useGuestCountActions();
  const handleResendInvite = async (invite: PendingGuestInvite) => {
    if (!displayEvent || !vessel) return;
    await resend(invite, vessel, { id: displayEvent.id });
  };

  const { mutateAsync: deleteAnswer } = useDeleteEventInviteAnswerMutation();
  const handleRemoveAnswer = async (answer: EventInviteAnswer) => {
    try {
      await deleteAnswer(answer.id);
      toast.success(`${answer.name} has been removed from the event.`);
    } catch (error) {
      console.error('Remove answered guest error:', error);
      toast.error('Couldn’t remove the guest. Please try again.');
    }
  };

  const handleRemoveGuest = async (guestId: string) => {
    if (!displayEvent) return;
    try {
      await deleteEventParticipantsApi(displayEvent.id, [guestId]);
      await getEventById(displayEvent.id, displayEvent.vessel_id);
      toast.success('Guest removed');
    } catch (error) {
      console.error('Remove guest error:', error);
      toast.error('Couldn’t remove the guest. Please try again.');
    }
  };

  const handleInviteGuest = async (
    invitees: GuestInvitee[],
    expiresAt: string | undefined,
    emailTemplate: InviteEmailTemplate,
    message: string | undefined,
  ) => {
    if (!displayEvent || !vessel) return;

    await inviteGuests({
      invitees,
      vessel,
      event: { id: displayEvent.id, expires_at: expiresAt },
      emailTemplate,
      message,
    });
    setIsGuestInvitationOpen(false);
  };

  const exportEventHandler = async () => {
    if (!event?.id) return;

    try {
      const blob = await getRequestBlob(API.ACTIVITY, `events/${event.id}/calendar`);
      const title = event.title.trim() || 'Event';
      await downloadBlob(blob, `${title}.ics`, event.title);
    } catch {
      toast.error('Couldn’t download the file. Please try again.');
    }
  };

  const typeLabel = EVENT_TYPES.find((item) => item.id === displayEvent?.type)?.label ?? 'Event';
  const TypeIcon = displayEvent?.type
    ? (TYPE_ICONS[displayEvent.type] ?? FaRegCalendar)
    : FaRegCalendar;

  // Every count is in people: each guest counts with the guests they bring.
  const guestParticipants = (displayEvent?.participants ?? []).filter((p) => p.type === 'guest');
  const confirmedCount =
    sumHeadcount(
      guestParticipants.filter((p) => p.status === 'accepted'),
      headcount,
    ) +
    sumHeadcount(
      answers.filter((a) => a.accepted),
      partyHeadcount,
    );
  const declinedCount =
    sumHeadcount(
      guestParticipants.filter((p) => p.status === 'declined'),
      headcount,
    ) +
    sumHeadcount(
      answers.filter((a) => !a.accepted),
      partyHeadcount,
    );
  // Guests added straight to the event (older events did that) have not answered
  // either — counted the way SubActivitySidePanel and TripDetailPage count them.
  const invitedCount =
    sumHeadcount(
      guestParticipants.filter((p) => p.status !== 'accepted' && p.status !== 'declined'),
      headcount,
    ) + sumHeadcount(pendingInvites, partyHeadcount);
  const allCount = confirmedCount + declinedCount + invitedCount;

  const heroTitle = displayEvent && <h2 className={s.heroTitle}>{displayEvent.title}</h2>;

  // Deliberately a sibling of the trip's drill-through link, never a child of it:
  // DeclinedBadge stops click propagation to open its reason modal, which would
  // starve Link's own preventDefault and turn the click into a native full-page
  // navigation. Badges are controls; a link may not contain one.
  const heroMeta = displayEvent && (
    <div className={s.heroMeta}>
      <span className={s.heroType}>
        <TypeIcon aria-hidden="true" /> {typeLabel}
      </span>
      {derivedStatus && <StatusPill label={derivedStatus.label} tone={derivedStatus.tone} />}
      {myMainStatus === 'unknown' && <PendingBadge />}
      {myMainStatus === 'declined' && <DeclinedBadge reason={myDeclineReason} />}
    </div>
  );

  const heroWhen = displayEvent?.time_slot && (
    <p className={s.heroWhen}>
      {formatEventDateRange(displayEvent.time_slot.start, displayEvent.time_slot.end)}
    </p>
  );

  return (
    <SidePanel open={open && !selectedSubActivityId} onClose={onClose} zIndex={1201}>
      {displayEvent && (
        <div className={s.root}>
          <header className={s.hero}>
            <div className={s.heroBar}>
              <button
                type="button"
                className={s.heroButton}
                onClick={onClose}
                aria-label="Close Event Details"
              >
                <IoMdClose />
              </button>
              {!isEventDeclined && (
                <button
                  type="button"
                  className={s.heroButton}
                  onClick={(e) => setMenuAnchor(e.currentTarget)}
                  aria-label="Event actions"
                >
                  <BsThreeDots />
                </button>
              )}
            </div>

            {displayEvent.image && (
              <AuthorizedImage
                src={imageVariantLink(displayEvent.image, 480, 180)}
                className={s.eventCover}
                alt={displayEvent.title}
              />
            )}

            {/* Trips are the only events with a page of their own, so only they get
                the drill-through — and the title/date block is the target rather
                than a link tucked under it. The standing "Open full trip details"
                line is what makes it discoverable without a hover. */}
            {displayEvent.type === EVENTS.TRIP ? (
              <>
                <Link
                  to={`/plan/trips/${displayEvent.id}`}
                  className={s.heroIdentity}
                  title={`Open ${displayEvent.title}`}
                  aria-label={`Open ${displayEvent.title} Details`}
                >
                  <div className={s.heroIdentityText}>
                    {heroTitle}
                    {heroWhen}
                    <span className={s.heroDrill}>
                      Open full trip details <BiChevronRight aria-hidden="true" />
                    </span>
                  </div>
                  <BiChevronRight className={s.heroChevron} aria-hidden="true" />
                </Link>
                {heroMeta}
              </>
            ) : (
              <>
                {heroTitle}
                {heroMeta}
                {heroWhen}
              </>
            )}
          </header>

          {displayEvent.description && (
            <section className={s.section}>
              <div className={`md-content ${s.description}`}>
                <ReactMarkdown>{displayEvent.description}</ReactMarkdown>
              </div>
            </section>
          )}

          <TagsSection event={displayEvent} canEdit={canActOnEvents} />

          {canInvite && (
            <section className={s.section} aria-label="Invite Link">
              <InviteLinkButton
                eventId={displayEvent.id}
                eventEndsAt={displayEvent.time_slot?.end}
              />
            </section>
          )}

          <section className={s.section} aria-label="Guests">
            <div className={s.sectionHead}>
              <span className={s.sectionTitle}>
                <HiOutlineUsers aria-hidden="true" /> Guests
              </span>
              <button
                type="button"
                className={s.sectionLink}
                onClick={() => setIsGuestsOpen((prev) => !prev)}
              >
                {isGuestsOpen ? 'Hide List' : 'View List'} <BiChevronRight aria-hidden="true" />
              </button>
            </div>
            <div className={s.stats}>
              <div className={s.stat}>
                <span className={s.statValue}>{allCount}</span>
                <span className={s.statLabel}>All</span>
              </div>
              <div className={s.stat}>
                <span className={s.statValue}>{confirmedCount}</span>
                <span className={s.statLabel}>
                  <span className={`${s.statDot} ${s.statConfirmed}`} aria-hidden="true" />
                  Confirmed
                </span>
              </div>
              <div className={s.stat}>
                <span className={s.statValue}>{invitedCount}</span>
                <span className={s.statLabel}>
                  <span className={`${s.statDot} ${s.statAwaiting}`} aria-hidden="true" />
                  Invited
                </span>
              </div>
              <div className={s.stat}>
                <span className={s.statValue}>{declinedCount}</span>
                <span className={s.statLabel}>
                  <span className={`${s.statDot} ${s.statDeclined}`} aria-hidden="true" />
                  Declined
                </span>
              </div>
            </div>
            {isGuestsOpen && (
              <GuestsSection
                guests={allGuests}
                participants={displayEvent.participants}
                isOwnerOrCaptain={isOwnerOrCaptain}
                currentUserId={currentUserId}
                showInvite={canInvite}
                onInviteClick={() => setIsGuestInvitationOpen(true)}
                pendingInvites={pendingInvites}
                answers={answers}
                onRemoveAnswer={(answer) => void handleRemoveAnswer(answer)}
                onRemoveGuest={(id) => void handleRemoveGuest(id)}
                onCancelInvite={(invite) => void handleCancelInvite(invite)}
                showDeadlines={!isGuest}
                onChangeDeadline={changeDeadline}
                deadlineMax={
                  displayEvent.time_slot?.end ? new Date(displayEvent.time_slot.end) : null
                }
                onChangeGuestCount={(target, n, name) =>
                  changeGuestCount(displayEvent.id, target, n, name)
                }
                // Hidden while one is in flight: a second tap would send a second invitation.
                onResendInvite={
                  isResending ? undefined : (invite) => void handleResendInvite(invite)
                }
              />
            )}
          </section>

          {groupedSubActivities.length > 0 && (
            <section className={s.section} aria-label="The Day">
              <div className={s.sectionHead}>
                <span className={s.sectionTitle}>The Day</span>
              </div>
              <div className={s.includedEventsList}>
                {groupedSubActivities.map(({ date, items }, groupIndex) => (
                  <EventDateGroup key={`${date.toISOString()}-${groupIndex}`} date={date}>
                    {items.map((subActivity) => (
                      <EventItem
                        key={subActivity.id}
                        compact
                        event={subActivity}
                        onClick={() => setSelectedSubActivityId(subActivity.id)}
                        status={
                          currentUserId
                            ? getParticipantStatus(subActivity?.participants, currentUserId)
                            : undefined
                        }
                        declineReason={
                          currentUserId
                            ? getParticipantDeclineReason(subActivity.participants, currentUserId)
                            : undefined
                        }
                        actions={
                          isGuest
                            ? myMainStatus === 'accepted'
                              ? [
                                  {
                                    label: 'Decline',
                                    icon: <MdOutlineCancel size={14} />,
                                    variant: 'danger',
                                    onClick: () => {
                                      subActivityToDecline.current = subActivity.id;
                                      setDeclineModalOpen(true);
                                    },
                                  },
                                ]
                              : undefined
                            : [
                                {
                                  label: 'Edit',
                                  icon: <BsPencil size={14} />,
                                  onClick: () => setSubActivityToEdit(subActivity),
                                },
                                {
                                  label: 'Delete',
                                  icon: <BiTrash size={14} />,
                                  variant: 'danger',
                                  onClick: () => setSubActivityToDelete(subActivity.id),
                                },
                              ]
                        }
                      />
                    ))}
                  </EventDateGroup>
                ))}
              </div>
            </section>
          )}

          {!isGuest && tasks.length > 0 && (
            <section className={s.section} aria-label="Tasks">
              <Accordion title={`Tasks (${tasks.length})`} openedByDefault>
                <TasksSection
                  tasks={tasks}
                  onTaskStatusChange={handleTaskStatusChange}
                  onEdit={handleEditTask}
                  onDelete={setTaskToDelete}
                  showAddTask
                  onAddTaskClick={() => {
                    setEditingTask(null);
                    setIsTaskSheetOpen(true);
                  }}
                />
              </Accordion>
            </section>
          )}

          {attachments.length > 0 && (
            <section className={s.section} aria-label="Photos">
              <Accordion title={`Photos (${attachments.length})`} openedByDefault>
                <Carousel>
                  <Carousel.Prev />
                  <Carousel.Track>
                    {attachments.map((img, i) => (
                      <Carousel.Slide
                        key={i}
                        src={getCarouselSlideSrc(img)}
                        onClick={() =>
                          setPreviewAttachment({
                            name: img.type === 'attachment' ? img.name : '',
                            src: getCarouselImageSrc(img),
                          })
                        }
                      />
                    ))}
                  </Carousel.Track>
                  <Carousel.Next />
                </Carousel>
              </Accordion>
            </section>
          )}

          <div className={s.actions}>
            {canPromote && (
              <Button type="button" onClick={openPromote}>
                Promote to Planned
              </Button>
            )}
            <EventChatButtons
              vesselId={vessel?.id}
              isGuest={isGuest}
              eventChatId={displayEvent?.temp_chat_id}
              closePanel={() => {
                onChatOpen?.();
                onClose();
              }}
            />
            {/* The menu page is a per-day planner; an undated trip has no days. */}
            {canViewMenu && !!displayEvent && isDated(displayEvent) && (
              <ButtonOutlined
                onClick={() => {
                  navigate(`/event-menu/${event?.id}`);
                }}
              >
                <MenuIcon width={20} /> Menu
              </ButtonOutlined>
            )}
          </div>

          <footer className={s.footer}>
            <button type="button" className={s.footerLink} onClick={exportEventHandler}>
              <FaRegCalendar aria-hidden="true" /> Add to calendar
            </button>
            <button
              type="button"
              className={s.footerLink}
              onClick={() => navigate('/safety-checklist')}
            >
              <BiErrorCircle aria-hidden="true" /> Emergency plan
            </button>
          </footer>
        </div>
      )}

      <ContextMenu
        isOpen={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
        anchorEl={menuAnchor}
        items={
          isGuest
            ? [
                ...(isEventPending
                  ? [
                      {
                        label: 'Accept',
                        icon: <MdOutlineCheckCircle size={14} />,
                        onClick: handleAccept,
                      },
                    ]
                  : []),
                {
                  label: 'Decline',
                  icon: <MdOutlineCancel size={14} />,
                  variant: 'danger' as const,
                  onClick: () => {
                    subActivityToDecline.current = null;
                    setMenuAnchor(null);
                    setDeclineModalOpen(true);
                  },
                },
              ]
            : [
                {
                  label: 'Edit',
                  icon: <BsPencil size={14} />,
                  onClick: () => event && onEdit?.(event.id),
                },
                ...(onDuplicate
                  ? [
                      {
                        label: 'Plan Similar Trip',
                        icon: <BiCopy size={14} />,
                        onClick: () => event && onDuplicate(event.id),
                      },
                    ]
                  : []),
                ...(isEventCompleted
                  ? []
                  : [
                      isEventCancelled
                        ? {
                            label: 'Renew',
                            icon: <MdOutlineRestartAlt size={14} />,
                            onClick: () => void handleRenewEvent(),
                          }
                        : {
                            label: 'Cancel',
                            icon: <MdOutlineCancel size={14} />,
                            onClick: () => {
                              setMenuAnchor(null);
                              setCancelModalOpen(true);
                            },
                          },
                    ]),
                {
                  label: 'Delete',
                  icon: <BsTrash size={14} />,
                  variant: 'danger' as const,
                  onClick: () => event && onDelete?.(event.id),
                },
              ]
        }
      />

      <SubActivitySidePanel
        subActivity={selectedSubActivity}
        parentEvent={displayEvent}
        onClose={() => setSelectedSubActivityId(null)}
        onChatOpen={() => {
          setSelectedSubActivityId(null);
          onChatOpen?.();
          onClose();
        }}
        onEditSubActivity={onEditSubActivity}
      />

      {subActivityToEdit && (
        <CreateItinerarySheet
          open
          onClose={() => setSubActivityToEdit(null)}
          initialData={{
            title: subActivityToEdit.title,
            description: subActivityToEdit.description,
            time_slot: {
              start: subActivityToEdit.time_slot.start,
              end: subActivityToEdit.time_slot.end,
            },
            participants: subActivityToEdit.participants,
          }}
          onSubmit={async (data) => {
            const payload: SubEventInput = {
              title: data.title,
              description: data.description,
              time_slot: {
                start: data.time_slot.start,
                end: data.time_slot.end,
              },
              participants: data.participants,
            };
            await updateSubActivity(subActivityToEdit.id, payload, displayEvent ?? null);
            onEditSubActivity?.(subActivityToEdit.id, payload);
            setSubActivityToEdit(null);
          }}
        />
      )}

      <ConfirmationModal
        open={!!subActivityToDelete}
        onClose={() => setSubActivityToDelete(null)}
        onConfirm={() => void confirmDeleteSubActivity()}
        title="Delete Activity"
        message={`Would you like to delete “${subActivityToDeleteTitle}”? This can’t be undone.`}
      />

      <ConfirmationModal
        open={!!taskToDelete}
        onClose={() => setTaskToDelete(null)}
        onConfirm={confirmDeleteTask}
        title="Delete Task"
        message={`Are you sure you want to delete “${taskToDeleteTitle}”? It can’t be undone.`}
      />

      <DeclineReasonModal
        open={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        onConfirm={(reason) => void handleCancelEvent(reason)}
        title="Cancel Event"
        reasonPlaceholder="Gale forecast for Saturday"
        submitLabel="Cancel Event"
      />

      <DeclineReasonModal
        open={declineModalOpen}
        onClose={() => {
          setDeclineModalOpen(false);
          subActivityToDecline.current = null;
        }}
        onConfirm={handleDecline}
      />

      {previewAttachment && (
        <DocumentPreviewModal
          document={{
            contentType: 'image/jpeg',
            name: previewAttachment.name,
            storageLink: previewAttachment.src,
          }}
          onClose={() => setPreviewAttachment(null)}
        />
      )}

      {isGuestInvitationOpen && displayEvent && (
        <GuestInvitationModal
          onClose={() => setIsGuestInvitationOpen(false)}
          onInvite={handleInviteGuest}
          eventName={displayEvent.title}
          eventEndsAt={displayEvent.time_slot?.end}
          excludeIds={(displayEvent.participants ?? []).map(({ person_id }) => person_id)}
        />
      )}

      <Modal open={promoteOpen} onClose={() => setPromoteOpen(false)} title="Promote to Planned">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: 16 }}>
          <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
            Please pick a date range for this trip. Once you set the dates, the trip becomes
            Planned.
          </p>
          <DateTimePicker
            label="Dates"
            placeholder="Choose dates"
            mandatory
            endRequired
            minDate={promoteMinDate}
            value={promoteStart ? promoteStart.toISOString() : null}
            endValue={promoteEnd ? promoteEnd.toISOString() : null}
            onRangeChange={(start, end) => {
              setPromoteStart(start ? new Date(start) : null);
              setPromoteEnd(end ? new Date(end) : null);
            }}
          />
          <Button
            type="button"
            disabled={!promoteStart || !promoteEnd || promoteSubmitting}
            onClick={() => void handlePromote()}
          >
            {promoteSubmitting ? 'Saving…' : 'Set Dates'}
          </Button>
        </div>
      </Modal>

      <CreateEditTaskSheet
        key={editingTask?.id ?? 'create'}
        open={isTaskSheetOpen}
        onClose={() => setIsTaskSheetOpen(false)}
        title={editingTask ? 'Edit Task' : 'Add Task'}
        hideWorkflowStatuses
        eventId={displayEvent?.id}
        initialData={editingTask ?? undefined}
        onSubmitTask={async (payload: CreateTaskPayload) => {
          if (editingTask) {
            await updateTask({ ...editingTask, ...payload } as unknown as TaskResponse);
          } else {
            await createTask(payload);
          }
          if (event?.id) {
            const updated = await getTasksByEventId(event.id);
            setTasks(updated);
          }
          setIsTaskSheetOpen(false);
        }}
      />
    </SidePanel>
  );
}

export default EventSidePanel;
